import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  IconButton,
  Chip,
  Skeleton,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Circle, Clock, Play, ArrowRight, History, RotateCcw, CalendarX2, CalendarClock, Info, X } from 'lucide-react';
import dayjs from 'dayjs';

// LocalStorage key for stopped tasks (array of recently stopped tasks)
const STOPPED_TASKS_KEY = 'nct_stopped_tasks';

import { taskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { useAppDispatch, useAppSelector } from '@/store';
import { selectActiveTimers, startTimerAsync } from '@/store/slices/timerSlice';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { Task, TaskStatus } from '@/types';
import PriorityBadge from '@/shared/components/PriorityBadge';
import ReopenedChip from '@/shared/components/ReopenedChip';

const MIDNIGHT_TIMER_TOOLTIP =
  'Running timers auto-stop at 12:00 AM (midnight) if they have not been stopped manually. Time is logged automatically.';

type TodayTasksTab = 'todo' | 'completed';

const MAX_VISIBLE_TASKS = 5;

const sortTodoTasks = (taskList: Task[], activeTimerTaskIds: string[]): Task[] => {
  const todayStr = dayjs().format('YYYY-MM-DD');
  return [...taskList].sort((a, b) => {
    const aTimerActive = activeTimerTaskIds.includes(a.id);
    const bTimerActive = activeTimerTaskIds.includes(b.id);
    if (aTimerActive && !bTimerActive) return -1;
    if (bTimerActive && !aTimerActive) return 1;

    const aIsRescheduled = a.rescheduledFromDates?.includes(todayStr) || false;
    const bIsRescheduled = b.rescheduledFromDates?.includes(todayStr) || false;

    if (aIsRescheduled && !bIsRescheduled) return 1;
    if (!aIsRescheduled && bIsRescheduled) return -1;

    if (a.status === TaskStatus.IN_PROGRESS && b.status !== TaskStatus.IN_PROGRESS) return -1;
    if (b.status === TaskStatus.IN_PROGRESS && a.status !== TaskStatus.IN_PROGRESS) return 1;

    const priorityOrder = { p1: 0, p2: 1, p3: 2, p4: 3 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });
};

const sortCompletedTasks = (taskList: Task[]): Task[] => {
  return [...taskList].sort((a, b) => {
    const aCompleted = a.completedAt ? dayjs(a.completedAt).valueOf() : 0;
    const bCompleted = b.completedAt ? dayjs(b.completedAt).valueOf() : 0;
    if (aCompleted !== bCompleted) return bCompleted - aCompleted;

    const priorityOrder = { p1: 0, p2: 1, p3: 2, p4: 3 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });
};

/**
 * TodaysTasksWidget
 * Shows tasks picked for today (To do / Completed).
 * Missed picks appear in Live/Overdue for re-pick.
 * Zero-friction design - primary actions always visible
 */
const TodaysTasksWidget: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const user = useAppSelector(selectCurrentUser);
  const activeTimers = useAppSelector(selectActiveTimers);
  const [removeDialog, setRemoveDialog] = useState<{ open: boolean; taskId: string | null; taskTitle: string | null }>({
    open: false,
    taskId: null,
    taskTitle: null,
  });
  const [removeError, setRemoveError] = useState('');
  const [activeTab, setActiveTab] = useState<TodayTasksTab>('todo');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: QUERY_KEYS.todaysTasks,
    queryFn: () => taskService.getTodaysPicks(),
    staleTime: 30000,
  });

  const activeTimerTaskIds = activeTimers.map((timer) => timer.taskId);

  React.useEffect(() => {
    if (activeTimerTaskIds.length > 0) {
      refetch();
    }
  }, [activeTimerTaskIds.join(','), refetch]);

  const tasks = data?.tasks ?? [];
  const tasksWithActiveTimers = React.useMemo(() => {
    const byId = new Map(tasks.map((task) => [task.id, task]));
    activeTimers.forEach((timer) => {
      if (!byId.has(timer.taskId)) {
        byId.set(timer.taskId, {
          id: timer.taskId,
          title: timer.taskTitle || 'Active task',
          status: TaskStatus.IN_PROGRESS,
          priority: 'p3',
          dueDate: dayjs().format('YYYY-MM-DD'),
          raci: {
            responsible: user?.id ? [user.id] : [],
            accountable: user?.id ?? '',
            consulted: [],
            informed: [],
          },
          blockedByTaskIds: [],
          blockingTaskIds: [],
          subtaskIds: [],
          order: 0,
          tags: [],
          createdAt: dayjs().toISOString(),
          updatedAt: dayjs().toISOString(),
          createdBy: user?.id ?? '',
        });
      }
    });
    return Array.from(byId.values());
  }, [tasks, activeTimers, user?.id]);
  const pickedCount = data?.pickedCount ?? 0;
  const totalPickedCount = data?.totalPickedCount ?? tasks.length;
  const carriedForwardCount = data?.carriedForwardCount ?? 0;
  const maxDailyPicks = data?.maxDailyPicks ?? 5;
  const canPickMore = data?.canPickMore ?? pickedCount < maxDailyPicks;

  const removeMutation = useMutation({
    mutationFn: (taskId: string) => taskService.removeFromToday(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todaysTasks });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.liveTasks });
      setRemoveDialog({ open: false, taskId: null, taskTitle: null });
      setRemoveError('');
    },
    onError: (err: Error) => {
      setRemoveError(err.message || 'Failed to remove task from today');
    },
  });

  const handleRequestRemove = (taskId: string, taskTitle: string) => {
    setRemoveDialog({ open: true, taskId, taskTitle });
  };

  const handleConfirmRemove = () => {
    if (removeDialog.taskId) {
      removeMutation.mutate(removeDialog.taskId);
    }
  };

  // Get stopped tasks from localStorage (array of tasks)
  const [stoppedTasks, setStoppedTasks] = React.useState<Array<{
    taskId: string;
    taskTitle: string;
    stoppedAt: string;
  }>>(() => {
    try {
      const stored = localStorage.getItem(STOPPED_TASKS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Listen for storage changes (cross-tab sync)
  React.useEffect(() => {
    const handleStorageChange = () => {
      try {
        const stored = localStorage.getItem(STOPPED_TASKS_KEY);
        setStoppedTasks(stored ? JSON.parse(stored) : []);
      } catch {
        setStoppedTasks([]);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleStartTimer = (taskId: string, taskTitle: string) => {
    if (user) {
      dispatch(startTimerAsync({ taskId, userId: user.id, taskTitle }));
      // Remove this task from stopped tasks list
      const updatedStoppedTasks = stoppedTasks.filter(t => t.taskId !== taskId);
      localStorage.setItem(STOPPED_TASKS_KEY, JSON.stringify(updatedStoppedTasks));
      setStoppedTasks(updatedStoppedTasks);
    }
  };

  /** Check if current user can use timer (ONLY Responsible users) */
  const canUseTimer = (task: Task): boolean => {
    const userId = user?.id;
    if (!userId) return false;
    return task.raci?.responsible?.includes(userId) || false;
  };

  // Track previous active timers to detect when any timer stops
  const previousTimerIds = React.useRef<string[]>(activeTimers.map(t => t.taskId));

  // Track when timers stop to update stopped tasks list
  React.useEffect(() => {
    const currentTimerIds = activeTimers.map(t => t.taskId);
    
    // Find tasks that were in previous but not in current (stopped tasks)
    const stoppedTaskIds = previousTimerIds.current.filter(
      id => !currentTimerIds.includes(id)
    );
    
    if (stoppedTaskIds.length > 0) {
      // Add newly stopped tasks to the list
      const newStoppedTasks = stoppedTaskIds
        .map(taskId => {
          const task = tasks.find(t => t.id === taskId);
          if (task) {
            return {
              taskId: task.id,
              taskTitle: task.title,
              stoppedAt: new Date().toISOString(),
            };
          }
          return null;
        })
        .filter(Boolean) as Array<{ taskId: string; taskTitle: string; stoppedAt: string }>;
      
      // Merge with existing stopped tasks (remove duplicates, keep latest)
      const updatedStoppedTasks = [
        ...newStoppedTasks,
        ...stoppedTasks.filter(st => !stoppedTaskIds.includes(st.taskId)),
      ];
      
      localStorage.setItem(STOPPED_TASKS_KEY, JSON.stringify(updatedStoppedTasks));
      setStoppedTasks(updatedStoppedTasks);
    }
    
    previousTimerIds.current = currentTimerIds;
  }, [activeTimers, tasks, stoppedTasks]);

  const handleViewAll = () => {
    navigate('/tasks');
  };

  const todayStr = dayjs().format('YYYY-MM-DD');
  const completedTasks = tasksWithActiveTimers.filter(t => t.status === TaskStatus.COMPLETED);
  const todoTasks = tasksWithActiveTimers.filter(t => t.status !== TaskStatus.COMPLETED);
  const rescheduledTasks = tasksWithActiveTimers.filter(t => t.rescheduledFromDates?.includes(todayStr));

  const sortedTodoTasks = sortTodoTasks(todoTasks, activeTimerTaskIds);
  const sortedCompletedTasks = sortCompletedTasks(completedTasks);
  const displayTasks = activeTab === 'completed' ? sortedCompletedTasks : sortedTodoTasks;
  const visibleTasks = displayTasks.slice(0, MAX_VISIBLE_TASKS);

  return (
    <Card sx={{ overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 1,
            mb: 2,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={600}>
                Today's Tasks
              </Typography>
              <Tooltip title={MIDNIGHT_TIMER_TOOLTIP}>
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', alignItems: 'center', color: 'text.secondary', cursor: 'help' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Info size={16} />
                </Box>
              </Tooltip>
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ wordBreak: 'break-word' }}
            >
              {pickedCount}/{maxDailyPicks} active picks
              {carriedForwardCount > 0 ? ` · ${carriedForwardCount} carried from yesterday` : ''}
              {totalPickedCount > pickedCount ? ` · ${totalPickedCount - pickedCount} completed` : ''}
              {!canPickMore && carriedForwardCount >= maxDailyPicks
                ? ' · no slots left for new picks'
                : ''}
              {rescheduledTasks.length > 0 ? ` · ${rescheduledTasks.length} rescheduled` : ''}
            </Typography>
          </Box>
          <Button
            size={isMobile ? 'small' : 'medium'}
            endIcon={<ArrowRight size={16} />}
            onClick={handleViewAll}
            sx={{ textTransform: 'none', flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
          >
            View All
          </Button>
        </Box>

        {tasksWithActiveTimers.length > 0 && (
          <Tabs
            value={activeTab}
            onChange={(_, value: TodayTasksTab) => setActiveTab(value)}
            sx={{ mb: 2, minHeight: 36 }}
            variant={isMobile ? 'fullWidth' : 'standard'}
          >
            <Tab
              value="todo"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <span>To do</span>
                  {todoTasks.length > 0 && (
                    <Chip label={todoTasks.length} size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                </Box>
              }
              sx={{ minHeight: 36, py: 0.5, textTransform: 'none' }}
            />
            <Tab
              value="completed"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <span>Completed</span>
                  {completedTasks.length > 0 && (
                    <Chip label={completedTasks.length} size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                </Box>
              }
              sx={{ minHeight: 36, py: 0.5, textTransform: 'none' }}
            />
          </Tabs>
        )}

        {/* Tasks List */}
        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={72} />
            ))}
          </Box>
        ) : tasksWithActiveTimers.length === 0 ? (
          <Box
            sx={{
              py: 6,
              textAlign: 'center',
              bgcolor: 'background.default',
              borderRadius: 2,
            }}
          >
            <CalendarClock size={48} style={{ color: '#94a3b8', marginBottom: 16 }} />
            <Typography variant="h6" gutterBottom>
              No tasks picked for today
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pick tasks from Live Tasks below to start your day
            </Typography>
          </Box>
        ) : displayTasks.length === 0 ? (
          <Box
            sx={{
              py: 4,
              textAlign: 'center',
              bgcolor: 'background.default',
              borderRadius: 2,
            }}
          >
            {activeTab === 'completed' ? (
              <>
                <CheckCircle size={40} style={{ color: '#94a3b8', marginBottom: 12 }} />
                <Typography variant="body1" fontWeight={500} gutterBottom>
                  No completed tasks yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Finished picks will appear here
                </Typography>
              </>
            ) : (
              <>
                <CheckCircle size={40} style={{ color: '#22c55e', marginBottom: 12 }} />
                <Typography variant="body1" fontWeight={500} gutterBottom>
                  All done for today
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {completedTasks.length > 0
                    ? 'Switch to Completed to review what you finished'
                    : 'Pick tasks from Live Tasks below to start your day'}
                </Typography>
              </>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              maxHeight: { xs: 'none', sm: 420 },
              overflowY: 'auto',
              overflowX: 'hidden',
              pr: 0.5,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: 'divider',
                borderRadius: '3px',
              },
            }}
          >
            <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {visibleTasks.map((task) => {
                const stoppedTask = stoppedTasks.find(st => st.taskId === task.id);
                const isRescheduled = task.rescheduledFromDates?.includes(todayStr) || false;
                return (
                  <TaskListItem
                    key={task.id}
                    task={task}
                    isTimerActive={activeTimers.some(t => t.taskId === task.id)}
                    isLastRunning={!!stoppedTask}
                    lastRunningSince={stoppedTask?.stoppedAt}
                    onStartTimer={() => handleStartTimer(task.id, task.title)}
                    onRemove={() => handleRequestRemove(task.id, task.title)}
                    isRemoving={removeMutation.isPending}
                    isRescheduled={isRescheduled}
                    missedDaysCount={task.missedDaysCount ?? 0}
                    isMissedPick={Boolean(task.isMissedPick)}
                    canTimer={canUseTimer(task)}
                    canRemove={!task.workedToday && !task.isCarriedForward}
                  />
                );
              })}
            </List>
          </Box>
        )}

        {/* Show more indicator */}
        {displayTasks.length > MAX_VISIBLE_TASKS && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button size="small" onClick={handleViewAll}>
              +{displayTasks.length - MAX_VISIBLE_TASKS} more {activeTab === 'completed' ? 'completed' : ''} tasks
            </Button>
          </Box>
        )}
      </CardContent>

      <Dialog
        open={removeDialog.open}
        onClose={() => !removeMutation.isPending && setRemoveDialog({ open: false, taskId: null, taskTitle: null })}
      >
        <DialogTitle>Remove from today?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove <strong>{removeDialog.taskTitle}</strong> from today&apos;s picks? You can pick it again later if you have picks remaining.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRemoveDialog({ open: false, taskId: null, taskTitle: null })}
            disabled={removeMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRemove}
            color="warning"
            variant="contained"
            disabled={removeMutation.isPending}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(removeError)}
        autoHideDuration={5000}
        onClose={() => setRemoveError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setRemoveError('')} sx={{ width: '100%' }}>
          {removeError}
        </Alert>
      </Snackbar>
    </Card>
  );
};

// Individual task item component
interface TaskListItemProps {
  task: Task;
  isTimerActive: boolean;
  isLastRunning?: boolean;
  lastRunningSince?: string;
  onStartTimer: () => void;
  onRemove: () => void;
  isRemoving?: boolean;
  isRescheduled?: boolean;
  missedDaysCount?: number;
  isMissedPick?: boolean;
  canTimer: boolean;
  canRemove?: boolean;
}

const TaskListItem: React.FC<TaskListItemProps> = ({
  task,
  isTimerActive,
  isLastRunning = false,
  lastRunningSince,
  onStartTimer,
  onRemove,
  isRemoving = false,
  isRescheduled = false,
  missedDaysCount = 0,
  isMissedPick = false,
  canTimer,
  canRemove = true,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleClick = () => {
    navigate(`/tasks?taskId=${task.id}`);
  };

  // Calculate deadline status
  const getDeadlineInfo = () => {
    const now = dayjs();
    const deadline = dayjs(task.dueDate);
    const diffHours = deadline.diff(now, 'hour');

    // If task is rescheduled from today, show rescheduled status
    if (isRescheduled) {
      return { label: `Rescheduled to ${deadline.format('MMM D')}`, color: 'warning' as const, isRescheduled: true };
    }
    
    if (task.status === TaskStatus.COMPLETED) {
      return { label: 'Completed', color: 'success' as const, isRescheduled: false };
    }
    if (diffHours < 0) {
      return { label: 'Overdue', color: 'error' as const, isRescheduled: false };
    }
    if (diffHours < 4) {
      return { label: `${diffHours}h left`, color: 'warning' as const, isRescheduled: false };
    }
    return { label: deadline.format('h:mm A'), color: 'default' as const, isRescheduled: false };
  };

  const deadlineInfo = getDeadlineInfo();
  const showsDueTime = /AM|PM/i.test(deadlineInfo.label);

  // Calculate time since last run
  const getTimeSinceLastRun = () => {
    if (!lastRunningSince) return '';
    const stoppedAt = dayjs(lastRunningSince);
    const now = dayjs();
    const diffMinutes = now.diff(stoppedAt, 'minute');
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  return (
    <ListItem
      sx={{
        px: { xs: 1.5, sm: 2 },
        py: 1.5,
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 0.75,
        cursor: 'pointer',
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: isRescheduled ? 'warning.50' : isLastRunning ? 'action.selected' : 'background.default',
        borderLeft: isRescheduled ? '3px solid' : isLastRunning ? '3px solid' : undefined,
        borderLeftColor: isRescheduled ? 'warning.main' : isLastRunning ? 'info.main' : undefined,
        opacity: isRescheduled ? 0.9 : 1,
        '&:hover': {
          bgcolor: isRescheduled ? 'warning.100' : isLastRunning ? 'action.selected' : 'action.hover',
        },
      }}
      onClick={handleClick}
      disablePadding
    >
      {/* Title row */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0, width: '100%' }}>
        <Box sx={{ flexShrink: 0, mt: 0.25 }}>
          {isRescheduled ? (
            <Tooltip title="Missed - Rescheduled">
              <CalendarX2 size={18} style={{ color: '#f59e0b' }} />
            </Tooltip>
          ) : task.status === TaskStatus.COMPLETED ? (
            <CheckCircle size={18} style={{ color: '#22c55e' }} />
          ) : task.status === TaskStatus.IN_PROGRESS ? (
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'white' }} />
            </Box>
          ) : (
            <Circle size={18} style={{ color: '#d4d4d4' }} />
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textDecoration: task.status === TaskStatus.COMPLETED || isRescheduled ? 'line-through' : 'none',
                color: task.status === TaskStatus.COMPLETED || isRescheduled ? 'text.secondary' : 'text.primary',
              }}
            >
              {task.title}
            </Typography>
            <ReopenedChip reopenedAt={task.reopenedAt} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <PriorityBadge priority={task.priority} size="small" />
              {task.isCarriedForward && (
                <Chip
                  label="Carried"
                  size="small"
                  color="secondary"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }}
                />
              )}
              {isRescheduled && (
                <Chip
                  label="MISS"
                  size="small"
                  color="error"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }}
                />
              )}
              {isMissedPick && missedDaysCount > 0 && (
                <Chip
                  label={`Missed · ${missedDaysCount}`}
                  size="small"
                  color="error"
                  sx={{
                    height: 20,
                    fontSize: '0.68rem',
                    fontWeight: 800,
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              )}
            </Box>
          </Box>

          {isRescheduled ? (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.25 }}>
              Was due today · Now due {dayjs(task.dueDate).format('MMM D')}
            </Typography>
          ) : (task.projectName || task.projectId) ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {task.projectName || task.projectId}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {/* Meta + actions row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          pl: { xs: 3.25, sm: 3.5 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minWidth: 0, flex: 1 }}>
          {isLastRunning && !isRescheduled && (
            <Chip
              size="small"
              icon={<History size={12} />}
              label={getTimeSinceLastRun()}
              color="info"
              variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem', maxWidth: '100%' }}
            />
          )}
          {isRescheduled ? (
            <Tooltip title="This task was due today but rescheduled. It counts as a miss for today.">
              <Chip
                size="small"
                icon={<CalendarClock size={12} />}
                label={deadlineInfo.label}
                color="warning"
                sx={{ height: 22, fontSize: '0.65rem', maxWidth: '100%' }}
              />
            </Tooltip>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
              <Chip
                size="small"
                icon={<Clock size={12} />}
                label={deadlineInfo.label}
                color={deadlineInfo.color}
                sx={{ height: 22, maxWidth: '100%' }}
              />
              {showsDueTime && task.status !== TaskStatus.COMPLETED && (
                <Tooltip title={MIDNIGHT_TIMER_TOOLTIP}>
                  <Box
                    component="span"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ display: 'inline-flex', alignItems: 'center', color: 'text.secondary', cursor: 'help' }}
                  >
                    <Info size={14} />
                  </Box>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 'auto' }}>
          {task.status !== TaskStatus.COMPLETED && !isRescheduled && canTimer && (
            <Tooltip title={isLastRunning ? 'Resume last task' : 'Start timer'}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onStartTimer();
                }}
                disabled={isTimerActive}
                sx={{
                  width: isMobile ? 36 : 32,
                  height: isMobile ? 36 : 32,
                  bgcolor: isTimerActive ? 'success.100' : isLastRunning ? 'info.50' : 'primary.50',
                  '&:hover': {
                    bgcolor: isTimerActive ? 'success.200' : isLastRunning ? 'info.100' : 'primary.100',
                  },
                }}
              >
                {isLastRunning ? (
                  <RotateCcw size={14} style={{ color: '#4F46E5' }} />
                ) : (
                  <Play
                    size={14}
                    fill={isTimerActive ? '#16a34a' : '#4F46E5'}
                    style={{ color: isTimerActive ? '#16a34a' : '#4F46E5' }}
                  />
                )}
              </IconButton>
            </Tooltip>
          )}
          {task.status !== TaskStatus.COMPLETED && !isRescheduled && canRemove && (
            <Tooltip
              title={
                isTimerActive
                  ? 'Stop the timer before removing from today'
                  : 'Remove from today (undo pick)'
              }
            >
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove();
                  }}
                  disabled={isTimerActive || isRemoving}
                  sx={{
                    width: isMobile ? 36 : 32,
                    height: isMobile ? 36 : 32,
                    bgcolor: 'action.hover',
                    '&:hover': { bgcolor: 'warning.50' },
                  }}
                >
                  <X size={14} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {task.status !== TaskStatus.COMPLETED && !isRescheduled && !canRemove && !isTimerActive && task.isCarriedForward && (
            <Tooltip title="Carried forward from a previous day — cannot remove">
              <span>
                <IconButton
                  size="small"
                  disabled
                  sx={{ width: isMobile ? 36 : 32, height: isMobile ? 36 : 32, opacity: 0.4 }}
                >
                  <X size={14} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {task.status !== TaskStatus.COMPLETED && !isRescheduled && !canRemove && !isTimerActive && !task.isCarriedForward && (
            <Tooltip title="Cannot remove after time has been logged on this task today">
              <span>
                <IconButton
                  size="small"
                  disabled
                  sx={{ width: isMobile ? 36 : 32, height: isMobile ? 36 : 32, opacity: 0.4 }}
                >
                  <X size={14} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
    </ListItem>
  );
};

export default TodaysTasksWidget;
